/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AdcsApiService, BackupApiService, ScheduleApiService } from '../../../shared/http';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import {
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    UtilsService
} from '../../../../../core/services';
import { NamespaceService, PortalRbacService } from '../../../shared/services/utils';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-schedules',
    templateUrl: './schedules.component.html',
    styleUrls: ['./schedules.component.scss']
})

export class SchedulesComponent extends BaseComponent {
    /**
     * Table row data
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url
     */
    public searchTerm: string | any[] = '';

    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        productType: [
            <DropdownModel>{ key: 'all', value: 'All' },
            <DropdownModel>{ key: 'adc', value: 'ADC' }
        ],
        scheduleType: [
            <DropdownModel>{ key: 'all', value: 'All' },
            <DropdownModel>{ key: 'backup', value: 'Backup' },
            <DropdownModel>{ key: 'update', value: 'Update' },
        ]
    };

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Store if tier is free.
     */
    public isFreeTier: boolean = true;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Mock table headers.
     */
    public mockTableHeaders: string[] = [
        'product_type', 'product_name', 'adc_running_status', 'scheduled_type', 'scheduled_date_time', 'occurrence'
    ];

    constructor(
        private adcApiService: AdcsApiService,
        private backupApiService: BackupApiService,
        private cdr: ChangeDetectorRef,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceService: NamespaceService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private portalRbacService: PortalRbacService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private scheduleApiService: ScheduleApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;

        this.setUpPageData();
        this.setUpSubscribers();
        this.setupSocketListeners();
        this.registerButtons();

        this.isFreeTier = this.portalRbacService.selectedTier?.tier === 'free';
    }

    /**
     * CTA to open intercom chat.
     */
    public contactSales(): void {
        Intercom('showNewMessage', 'I’m on the free tier in portal and I’m interested in upgrading. Can I speak to sales?');
    }

    /**
     * Get image src based on running status.
     * @param row
     */
    public getRunningStatusSrc(row: any): string {
        let statusName: string;
        if (row.adoptionStatus !== 'errorConnectionTimeout') {
            switch (row.adcStatus) {
                case 'unknown':
                case 'offline':
                    statusName = 'disconnected';
                    break;
                case 'pending':
                    statusName = 'pending';
                    break;
                default:
                    statusName = 'connected';
            }
        } else {
            statusName = 'pending';
        }

        return `./assets/centralisedManagement/icons/running-status-${statusName}.svg`;
    }

    /**
     * Set colour for ADC status.
     * @param row
     */
    public getAdcStatusClassName(row: any): string {
        if (row.adcStatus !== 'offline' && row.adcStatus !== 'unknown') {
            return 'online';
        } else {
            return 'disconnected';
        }
    }

    /**
     * Get occurrence name by index.
     * @param index
     */
    public getOccurrenceName(index: string | number): string {
        const occurrences = {
            0: 'Once',
            1: 'Daily',
            7: 'Weekly',
            30: 'Monthly',
            365: 'Yearly'
        };

        return occurrences[index];
    }

    /**
     * Run row task helper.
     * @param row
     */
    public runTask(row: any): void {
        this.adcApiService.getAll(this.selectedNamespaceKey)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (adcs: any): void => {
                    if (this.adcExists(adcs, row)) {
                        this.runTaskNow(row);
                    } else {
                        this.logService.error('SchedulesComponent:runTask', 'No ADC with this ID exists');
                        this.loadingService.loadingActionDotsEmitter.next(false);
                        this.notifyService.error('scheduler_error_message_no_adc', { messageTranslate: true });
                    }
                },
                error: (error: ErrorResponseModel) => this.logService.error('AdcsListComponent: runTask', error.message)
            })
            .add(
                () => this.loadingService.loadingActionDotsEmitter.next(false)
            );
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'schedule-list-add-new': [
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET', 'POST'] },
            ],
            'schedule-list-edit-schedule': [
                { path: '/api/v1/:namespace/scheduler/:id', methods: ['PATCH'] },
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] },
            ],
            'schedule-list-delete-schedule': [
                { path: '/api/v1/:namespace/scheduler/:id', methods: ['DELETE'] }
            ],
            'schedule-list-view-backups': [
                { path: '/api/v1/:namespace/backups/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] },
            ],
            'schedule-list-backup-update-adc': [
                { path: '/api/v1/:namespace/adcs/:adcID/backup', methods: ['POST'] },
            ],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Check if ADC still exists.
     * @param adcs
     * @param row
     * @private
     */
    private adcExists(adcs: any[], row: any): boolean {
        return adcs.some(item => item.id === row.productId);
    }

    /**
     * Run task now based on row.
     * @param row
     * @private
     */
    private runTaskNow(row: any): void {
        if (row.scheduleType === 'backup') {
            this.backupApiService.backupAdc(row.namespaceId, row.productId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => row.adcStatus = 'backup',
                    error: (error: ErrorResponseModel) => this.logService.error(
                        'SchedulesComponent: backupAdc',
                        error.message
                    )
                })
                .add(
                    () => this.loadingService.loadingActionDotsEmitter.next(false)
                );
        }

        if (row.scheduleType === 'update') {
            this.adcApiService.systemUpdate(row.namespaceId, row.productId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => row.adcStatus = 'updating',
                    error: (error: ErrorResponseModel) => this.logService.error(
                        'SchedulesComponent: systemUpdate',
                        'Error submitting data', error
                    )
                })
                .add(
                    () => this.loadingService.loadingActionDotsEmitter.next(false)
                );
        }
    }

    /**
     * Edit schedule.
     * @param row
     */
    public editSchedule(row: any): void {
        this.router.navigate(['cm/scheduler/edit'], { queryParams: { id: row.id } }).then().catch();
    }

    /**
     * Confirm delete schedule.
     * @param row
     */
    public confirmDeleteSchedule(row: any): void {
        const confirmationObject = {
            title: 'schedule_delete',
            message: 'schedule_delete_message',
            callBack: this.deleteSchedule.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete schedule.
     * @param row
     * @private
     */
    private deleteSchedule(row: any): Observable<any> {
        return new Observable((observer) => {
            this.scheduleApiService.deleteSchedule(this.selectedNamespaceKey, row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('ScheduleListComponent:deleteSchedule', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get list of schedules.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            schedules: this.scheduleApiService.getAllSchedules(this.selectedNamespaceKey),
            adcs: this.adcApiService.getAll(this.selectedNamespaceKey),
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any) => {
                    if (dataGroup['schedules'] && dataGroup['adcs']) {
                        this.populateTableRows(dataGroup['schedules'], dataGroup['adcs']);
                    }
                    this.checkForSearchFilters();
                    this.isLoading = false;
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.error('ScheduleListComponent:getScheduleList', error.message);
                }
            });
    }

    /**
     * Populate table rows.
     * @private
     */
    private populateTableRows(schedules: any[], products: any[]): void {
        const tableRows = [];

        for (const schedule of schedules) {
            const product = products.find(item => item.id === schedule.productID);
            const productType = schedule.product === 'adc' ? schedule.product.toUpperCase() : schedule.product;

            tableRows.push({
                id: schedule.id,
                productType: productType,
                productName: product && product.label,
                productId: product.id,
                scheduleType: schedule.scheduleType,
                scheduleTime: this.utilsService.createDateTimeString(schedule.scheduleTime),
                occurrence: schedule.occurrence,
                adcStatus: product.status,
                namespaceId: product.namespaceID || product.namespaceId,
                status: this.getStatusName(schedule.status),
                occurrenceName: this.getOccurrenceName(schedule.occurrence)
            });
        }

        const sortOrder = ['failed', 'timed out', 'pending', 'running', 'completed'];

        this.tableRows = [...this.utilsService.sortArrayByProperty(tableRows, 'status', sortOrder)];
    }

    /**
     * Get status friendly name.
     * @param status
     * @private
     */
    private getStatusName(status: any): string {
        switch (status) {
            case 1:
                return 'pending';
            case 2:
                return 'running';
            case 3:
                return 'completed';
            case 4:
                return 'failed';
            case 5:
                return 'timed out';
            case 6:
                return 'errored';
            default:
                return 'no status';
        }
    }

    /**
     * Helper function for filtering tableRows.
     * @private
     */
    private checkForSearchFilters(): void {
        const query = this.getSearchQueryInUrl();
        if (query) {
            this.searchTerm = query;
            this.navigationService.removeParamsFromUrl();
            this.cdr.detectChanges();
        }
    }

    /**
     * See if the URL has a search query parameter and assign it and search if it does.
     * @private
     */
    private getSearchQueryInUrl(): string {
        return this.utilsService.getObjectValue(this.route, 'snapshot.queryParamMap.params.sFilter');
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setUpSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any) => {
                    this.selectedNamespaceKey = namespace.key;
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.error(
                        'ScheduleListComponent: setupSubscribers',
                        'Unable to get the selected namespace',
                        error.message
                    );
                }
            });
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        // TODO waiting for the correct listener
        // this.socketService.on('ADCStateChange')
        //     .pipe(takeUntil(this.destroyed$))
        //     .subscribe(
        //         response => {
        //             this.updateStatus(response);
        //         }
        //     );
    }

    /**
     * Updated the status in the table data.
     * @param response
     * @private
     */
    private updateStatus(response: any): void {
        const data = response?.data;
        if (data?.id && data.adcStatus) {
            const index = this.tableRows.findIndex(item => item.productId === data.id);
            if (index !== -1 && (data.adcStatus === 'updating' || data.adcStatus === 'backup')) {
                this.tableRows[index].status = 'running';
            } else {
                this.tableRows[index].status = 'completed';
            }
        }
    }
}
